"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Grouping = void 0;
/**
 * Collection class which groups a set of data (T) and subgroups (Grouping<T>) together and optionally provides
 * descriptive metadata (M).
 */
var Grouping = /** @class */ (function () {
    function Grouping(groupName, children, metadata) {
        var _this = this;
        if (metadata === void 0) { metadata = {}; }
        this.groupName = groupName;
        this.children = children;
        this.metadata = metadata;
        /**
         * Filter the potentially recursive grouping
         * @param itemFilter optional boolean filter function applied to 'leaf' nodes (T)
         * @param groupFilter optional boolean filter function applied to a group AFTER all item filters are applied to its children
         * @returns new instance of Grouping containing filtered children
         */
        this.filter = function (itemFilter, groupFilter) {
            var iFilter = itemFilter || (function () { return true; });
            var gFilter = groupFilter || (function () { return true; });
            var filteredChildren = [];
            _this.children.forEach(function (d) {
                if (d instanceof Grouping) {
                    var newGroup = d.filter(itemFilter, groupFilter);
                    if (newGroup) {
                        filteredChildren.push(newGroup);
                    }
                }
                else if (iFilter(d)) {
                    filteredChildren.push(d);
                }
            });
            var output = new Grouping(_this.groupName, filteredChildren, _this.metadata);
            return gFilter(output) ? output : null;
        };
        /**
         * Flattens the potentially recursive grouping into an array, using an optional filter function
         * @param itemFilter optional boolean filter function applied to 'leaf' nodes (T)
         * @returns array of children (T)
         */
        this.flatten = function (itemFilter) {
            var iFilter = itemFilter || (function () { return true; });
            var results = [];
            _this.children.forEach(function (d) {
                if (d instanceof Grouping) {
                    results.push.apply(results, d.flatten(iFilter));
                }
                else if (iFilter(d)) {
                    results.push(d);
                }
            });
            return results;
        };
        /**
         * Map data and groupings to arbitrary new objects (U and N respectively). Can be used as a reducer.
         * @param itemTransform optional mapping function to apply to 'leaf' nodes (T)
         * @param groupTransform optional mapping function to transform groups given a list of already transformed children and old metadata
         * @returns mapped group (N)
         */
        this.map = function (itemTransform, groupTransform) {
            var iTransform = itemTransform || (function (item) { return item; });
            var gTransform = groupTransform || (function (n, c, m) { return new Grouping(n, c, m); });
            return _this.pvtMap(iTransform, gTransform, []);
        };
        /**
         * Map child data and groupings to arbitrary new objects (U and N respectively).
         * @param itemTransform optional mapping function to apply to 'leaf' nodes (T)
         * @param groupTransform optional mapping function to transform groups given a list of already transformed children and old metadata
         * @returns array of transformed children
         */
        this.mapChildren = function (itemTransform, groupTransform) {
            var iTransform = itemTransform || (function (item) { return item; });
            var gTransform = groupTransform || (function (n, c, m) { return new Grouping(n, c, m); });
            return _this.children.map(function (c, index) {
                if (c instanceof Grouping) {
                    return c.pvtMap(iTransform, gTransform, [index]);
                }
                else {
                    return iTransform(c, [index]);
                }
            });
        };
        /**
         * Find and return first item for which the predicate function returns true along with its position in the tree.
         * This is a depth-first search.
         * @param predicate the function to apply to each item
         * @returns 2-tuplet of the found item and its tree position as an array of indices
         */
        this.findItem = function (predicate) {
            return _this.recursiveFind(predicate, false, []) || [undefined, undefined];
        };
        /**
         * Find and return first group for which the predicate function returns true along with its position in the tree.
         * This is a depth-first search.
         * @param predicate the function to apply to each group
         * @returns 2-tuplet of the found group and its tree position as an array of indices
         */
        this.findGroup = function (predicate) {
            return _this.recursiveFind(predicate, true, []) || [undefined, undefined];
        };
        /**
         * Run a function for each item and/or group in a grouping
         * @param itemFunc (optional) function to apply to each item, given the item itself and its tree position
         * @param groupFunc (optional) function to apply to each group, given the group itself and its tree position
         */
        this.forEach = function (itemFunc, groupFunc) {
            var iFunc = itemFunc ||
                (function () {
                    return;
                });
            var gFunc = groupFunc ||
                (function () {
                    return;
                });
            return _this.recursiveForEach(iFunc, gFunc, []);
        };
        this.pos = function (treePos) {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            var cursor = _this;
            for (var i = 0; i < treePos.length; i++) {
                if (i === treePos.length - 1) {
                    return cursor.children[treePos[i]];
                }
                else {
                    var newCursor = cursor.children[treePos[i]];
                    if (newCursor === undefined || !(newCursor instanceof Grouping)) {
                        return;
                    }
                    else {
                        cursor = newCursor;
                    }
                }
            }
            return;
        };
        // PRIVATE FUNCTIONS ---------------------------
        /**
         * Recursive portion of this.map
         */
        this.pvtMap = function (itemTransform, groupTransform, treePos) {
            var mappedChildren = _this.children.map(function (c, index) {
                var newTreePos = treePos.slice();
                newTreePos.push(index);
                if (c instanceof Grouping) {
                    return c.pvtMap(itemTransform, groupTransform, newTreePos);
                }
                else {
                    return itemTransform(c, newTreePos);
                }
            });
            return groupTransform(_this.groupName, mappedChildren, _this.metadata, treePos);
        };
        /**
         * Recursive portion of this.forEach
         */
        this.recursiveForEach = function (itemFunc, groupFunc, treePos) {
            _this.children.forEach(function (child, index) {
                var newTreePos = __spreadArray(__spreadArray([], treePos, true), [index], false);
                if (child instanceof Grouping) {
                    child.recursiveForEach(itemFunc, groupFunc, newTreePos);
                }
                else {
                    itemFunc(child, newTreePos);
                }
            });
            groupFunc(_this, treePos);
        };
        /**
         * Recursive find, used by both this.findGroup and this.findItem
         */
        this.recursiveFind = function (predicate, findGroup, treePos) {
            if (findGroup && predicate(_this)) {
                return [_this, treePos];
            }
            else {
                for (var i = 0; i < _this.children.length; i++) {
                    var child = _this.children[i];
                    var newTreePos = __spreadArray(__spreadArray([], treePos, true), [i], false);
                    if (child instanceof Grouping) {
                        var result = child.recursiveFind(predicate, findGroup, newTreePos);
                        if (result) {
                            return result;
                        }
                    }
                    if (!(child instanceof Grouping) && !findGroup) {
                        if (predicate(child)) {
                            return [child, newTreePos];
                        }
                    }
                }
            }
            return;
        };
    }
    return Grouping;
}());
exports.Grouping = Grouping;
